// IndexedDB utility for task management
const DB_NAME = "ProductivityTimerDB";
const DB_VERSION = 1;

// Store names
const STORES = {
  PROJECTS: "projects",
  TASKS: "tasks",
  TIMER_SESSIONS: "timerSessions",
};

class Database {
  constructor() {
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = async () => {
        this.db = request.result;
        // Run migration to fix tasks missing createdAt
        try {
          await this.migrateTasks();
        } catch (error) {
          console.warn("Migration failed:", error);
        }
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Projects store
        if (!db.objectStoreNames.contains(STORES.PROJECTS)) {
          const projectsStore = db.createObjectStore(STORES.PROJECTS, {
            keyPath: "id",
          });
          projectsStore.createIndex("name", "name", { unique: false });
        }

        // Tasks store
        if (!db.objectStoreNames.contains(STORES.TASKS)) {
          const tasksStore = db.createObjectStore(STORES.TASKS, {
            keyPath: "id",
          });
          tasksStore.createIndex("projectId", "projectId", { unique: false });
          tasksStore.createIndex("status", "status", { unique: false });
          tasksStore.createIndex("category", "category", { unique: false });
        }

        // Timer sessions store
        if (!db.objectStoreNames.contains(STORES.TIMER_SESSIONS)) {
          const sessionsStore = db.createObjectStore(STORES.TIMER_SESSIONS, {
            keyPath: "id",
          });
          sessionsStore.createIndex("taskId", "taskId", { unique: false });
          sessionsStore.createIndex("startTime", "startTime", {
            unique: false,
          });
        }
      };
    });
  }

  // Generic CRUD operations
  async add(storeName, data) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const request = store.add(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async get(storeName, id) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], "readonly");
      const store = transaction.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(storeName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], "readonly");
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async update(storeName, data) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName, id) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Project operations
  async addProject(project) {
    const projectData = {
      id: this.generateId(),
      name: project.name,
      color: project.color || "#97feed",
      createdAt: new Date().toISOString(),
    };
    await this.add(STORES.PROJECTS, projectData);
    return projectData;
  }

  async getProjects() {
    return this.getAll(STORES.PROJECTS);
  }

  async updateProject(project) {
    return this.update(STORES.PROJECTS, project);
  }

  async deleteProject(projectId) {
    // Also delete all tasks in this project
    const tasks = await this.getTasksByProject(projectId);
    for (const task of tasks) {
      await this.deleteTask(task.id);
    }
    return this.delete(STORES.PROJECTS, projectId);
  }

  // Task operations
  async addTask(task) {
    const taskData = {
      id: this.generateId(),
      projectId: task.projectId,
      title: task.title,
      description: task.description || "",
      category: task.category || "",
      status: task.status || "todo",
      timeTracked: task.timeTracked || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await this.add(STORES.TASKS, taskData);
    return taskData;
  }

  async getTasks() {
    const tasks = await this.getAll(STORES.TASKS);
    // Migrate tasks that are missing createdAt field
    const migratedTasks = tasks.map((task) => {
      if (!task.createdAt) {
        console.log("🔧 DB: Migrating task missing createdAt:", task.id);
        return {
          ...task,
          createdAt: task.updatedAt || new Date().toISOString(),
          updatedAt: task.updatedAt || new Date().toISOString(),
        };
      }
      return task;
    });

    // Update any migrated tasks in the database
    for (const task of migratedTasks) {
      if (!tasks.find((t) => t.id === task.id)?.createdAt) {
        await this.update(STORES.TASKS, task);
        console.log(
          "✅ DB: Migrated task:",
          task.id,
          "with createdAt:",
          task.createdAt
        );
      }
    }

    return migratedTasks;
  }

  async getTasksByProject(projectId) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.TASKS], "readonly");
      const store = transaction.objectStore(STORES.TASKS);
      const index = store.index("projectId");
      const request = index.getAll(projectId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateTask(task) {
    const updatedTask = {
      ...task,
      createdAt: task.createdAt || new Date().toISOString(), // Ensure createdAt exists
      updatedAt: new Date().toISOString(),
    };
    return this.update(STORES.TASKS, updatedTask);
  }

  async deleteTask(taskId) {
    // Also delete all timer sessions for this task
    const sessions = await this.getTimerSessionsByTask(taskId);
    for (const session of sessions) {
      await this.deleteTimerSession(session.id);
    }
    return this.delete(STORES.TASKS, taskId);
  }

  // Migration function to fix tasks missing createdAt
  async migrateTasks() {
    console.log("🔧 DB: Starting task migration...");
    const tasks = await this.getAll(STORES.TASKS);
    let migratedCount = 0;

    for (const task of tasks) {
      if (!task.createdAt) {
        const migratedTask = {
          ...task,
          createdAt: task.updatedAt || new Date().toISOString(),
          updatedAt: task.updatedAt || new Date().toISOString(),
        };
        await this.update(STORES.TASKS, migratedTask);
        migratedCount++;
        console.log(
          "✅ DB: Migrated task:",
          task.id,
          "with createdAt:",
          migratedTask.createdAt
        );
      }
    }

    console.log(`🎉 DB: Migration complete! Fixed ${migratedCount} tasks.`);
    return migratedCount;
  }

  // Timer session operations
  async addTimerSession(session) {
    // Check for potential duplicates (same startTime and duration within 1 second)
    const existingSessions = await this.getAll(STORES.TIMER_SESSIONS);
    const duplicate = existingSessions.find(
      (existing) =>
        existing.startTime === session.startTime &&
        Math.abs(existing.duration - session.duration) <= 1
    );

    if (duplicate) {
      console.warn("Duplicate timer session detected, skipping save");
      return duplicate;
    }

    const sessionData = {
      id: this.generateId(),
      taskId: session.taskId,
      duration: session.duration,
      startTime: session.startTime,
      endTime: session.endTime || new Date().toISOString(),
    };
    await this.add(STORES.TIMER_SESSIONS, sessionData);

    // Update task's total time tracked
    if (session.taskId) {
      const task = await this.get(STORES.TASKS, session.taskId);
      if (task) {
        task.timeTracked += session.duration;
        await this.updateTask(task);
      }
    }

    return sessionData;
  }

  async getTimerSessions() {
    return this.getAll(STORES.TIMER_SESSIONS);
  }

  async getTimerSessionsByTask(taskId) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(
        [STORES.TIMER_SESSIONS],
        "readonly"
      );
      const store = transaction.objectStore(STORES.TIMER_SESSIONS);
      const index = store.index("taskId");
      const request = index.getAll(taskId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteTimerSession(sessionId) {
    return this.delete(STORES.TIMER_SESSIONS, sessionId);
  }

  // Utility methods
  generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Export all data
  async exportData() {
    const projects = await this.getProjects();
    const tasks = await this.getTasks();
    const sessions = await this.getTimerSessions();

    return {
      projects,
      tasks,
      sessions,
      exportDate: new Date().toISOString(),
    };
  }

  // Import data
  async importData(data) {
    // Clear existing data
    await this.clearAllData();

    // Import projects
    for (const project of data.projects || []) {
      await this.add(STORES.PROJECTS, project);
    }

    // Import tasks
    for (const task of data.tasks || []) {
      await this.add(STORES.TASKS, task);
    }

    // Import sessions
    for (const session of data.sessions || []) {
      await this.add(STORES.TIMER_SESSIONS, session);
    }
  }

  async clearAllData() {
    const transaction = this.db.transaction(
      [STORES.PROJECTS, STORES.TASKS, STORES.TIMER_SESSIONS],
      "readwrite"
    );

    await transaction.objectStore(STORES.PROJECTS).clear();
    await transaction.objectStore(STORES.TASKS).clear();
    await transaction.objectStore(STORES.TIMER_SESSIONS).clear();
  }
}

// Create singleton instance
const db = new Database();

export default db;
